import {Injectable} from '@angular/core';
import {HttpHeaders} from '@angular/common/http';
import {AuthStateService} from '@auth/states';
import {CompanyStateService} from '@core/states';
import {ApiCollection, ApiResponse, Attachment, TaskAssignee, TaskItem} from '@core/types';
import {ENDPOINTS, GET_URL, log} from '@core/utils';
import {HttpService} from '@core/services';

@Injectable({
  providedIn: 'root'
})
export class TaskService {
  constructor(
    private apiService: HttpService,
    private companyState: CompanyStateService,
    private authState: AuthStateService
  ) {}

  async getTasks(page = 1, itemsPerPage = 50, filters: {
    status?: string;
    priority?: string;
    search?: string;
  } = {}, options?: {skipLoader?: boolean}): Promise<ApiResponse<TaskItem[]>> {
    try {
      const url = GET_URL(ENDPOINTS.TASKS, {
        page,
        itemsPerPage,
        ...filters
      });
      const headers = this.buildHeaders(options?.skipLoader);
      const response: ApiCollection<any> = await this.apiService.get(url, headers ? {headers} : undefined);
      const tasks = this.apiService.normalizeCollection<any>(response).member.map((item: any) => this.mapTask(item));
      return {success: true, result: tasks};
    } catch (error: any) {
      log.error('Error loading tasks:', error);
    }
    return {success: false, message: 'No se pudieron cargar las tareas.'};
  }

  async createTask(payload: {
    name: string;
    description?: string;
    status?: string;
    priority?: string;
    startDate?: string | null;
    endDate?: string | null;
  }, options?: {skipLoader?: boolean}): Promise<ApiResponse<TaskItem>> {
    try {
      const url = GET_URL(ENDPOINTS.TASKS);
      const headers = this.buildHeaders(options?.skipLoader);
      const response: any = await this.apiService.post(url, payload, headers ? {headers} : undefined);
      return {success: true, result: this.mapTask(response)};
    } catch (error: any) {
      log.error('Error creating task:', error);
    }
    return {success: false, message: 'No se pudo crear la tarea.'};
  }

  async getTask(taskId: string | number, options?: {skipLoader?: boolean}): Promise<ApiResponse<TaskItem>> {
    try {
      const url = GET_URL(ENDPOINTS.TASK_DETAIL, {id: taskId});
      const headers = this.buildHeaders(options?.skipLoader);
      const response: any = await this.apiService.get(url, headers ? {headers} : undefined);
      return {success: true, result: this.mapTask(response)};
    } catch (error: any) {
      log.error('Error loading task detail:', error);
    }
    return {success: false, message: 'No se pudo cargar la tarea.'};
  }

  async updateTask(taskId: string | number, payload: {
    name?: string;
    description?: string;
    status?: string;
    priority?: string;
    startDate?: string | null;
    endDate?: string | null;
  }, options?: {skipLoader?: boolean}): Promise<ApiResponse<TaskItem>> {
    try {
      const url = GET_URL(ENDPOINTS.TASK_DETAIL, {id: taskId});
      const headers = (this.buildHeaders(options?.skipLoader) ?? new HttpHeaders())
        .set('Content-Type', 'application/merge-patch+json');
      const response: any = await this.apiService.patch(url, payload, {headers, json: false});
      return {success: true, result: this.mapTask(response)};
    } catch (error: any) {
      log.error('Error updating task:', error);
    }
    return {success: false, message: 'No se pudo actualizar la tarea.'};
  }

  async deleteTask(taskId: string | number, options?: {skipLoader?: boolean}): Promise<ApiResponse<undefined>> {
    try {
      const url = GET_URL(ENDPOINTS.TASK_DETAIL, {id: taskId});
      const headers = this.buildHeaders(options?.skipLoader);
      await this.apiService.delete(url, headers ? {headers} : undefined);
      return {success: true};
    } catch (error: any) {
      log.error('Error deleting task:', error);
    }
    return {success: false, message: 'No se pudo eliminar la tarea.'};
  }

  async setAssignees(taskId: string | number, assigneeIds: Array<string | number>, options?: {skipLoader?: boolean}): Promise<ApiResponse<TaskItem>> {
    try {
      const url = GET_URL(ENDPOINTS.TASK_ASSIGNEES, {id: taskId});
      const headers = this.buildHeaders(options?.skipLoader);
      const userId = assigneeIds[0];
      const response: any = await this.apiService.post(url, {userId}, headers ? {headers} : undefined);
      return {success: true, result: this.mapTask(response)};
    } catch (error: any) {
      log.error('Error setting task assignees:', error);
    }
    return {success: false, message: 'No se pudo asignar responsables.'};
  }

  private mapTask(item: any): TaskItem {
    const attachments = this.mapAttachments(item);
    const assignees = this.mapAssignees(item);
    const assignedTo = item.assignedTo ? this.mapAssignee(item.assignedTo) : null;
    const startDate = item.startDate || item.start_at || item.start;
    const endDate = item.endDate || item.end_at || item.dueDate || item.end;
    const responsibles = assignees.length
      ? assignees.map(person => person.name).join(', ')
      : assignedTo?.name || '';
    const date = endDate || startDate || '';
    return {
      id: item.id,
      name: item.name,
      planId: item.plan?.id || item.planId || undefined,
      description: item.description,
      status: item.status,
      priority: item.priority,
      startDate,
      endDate,
      attachments,
      assignees,
      assignedTo,
      date,
      responsibles
    };
  }

  private mapAttachments(item: any): Attachment[] {
    const list = item.attachments || item.attachmentSummaries || item.documents || item.files || [];
    if (!Array.isArray(list)) return [];
    return list.map((entry: any) => ({
      '@context': entry['@context'],
      '@id': entry['@id'],
      '@type': entry['@type'],
      id: entry.id,
      fileName: entry.fileName || entry.name || entry.filename || 'Documento',
      fileSize: entry.fileSize || entry.size || entry.bytes,
      mimeType: entry.mimeType || entry.type,
      filePath: entry.filePath || entry.path,
      createdAt: entry.createdAt || entry.created_at,
      uploadedById: entry.uploadedById,
      uploadedByType: entry.uploadedByType
    }));
  }

  private mapAssignees(item: any): TaskAssignee[] {
    const list = item.assignees || item.assigned || item.responsibles || [];
    if (!Array.isArray(list)) return [];
    return list.map((person: any) => this.mapAssignee(person));
  }

  private mapAssignee(person: any): TaskAssignee {
    if (typeof person === 'string') {
      return {
        id: this.extractResourceId(person),
        name: 'Sin nombre'
      };
    }

    const firstName = person.firstName || '';
    const lastName = person.lastName || '';
    const name = person.name || `${firstName} ${lastName}`.trim() || 'Sin nombre';
    return {
      id: person.id || this.extractResourceId(person['@id'] || person.iri || ''),
      name,
      email: person.email
    };
  }

  private extractResourceId(value: string): string {
    const normalized = String(value || '').trim();
    if (!normalized) return '';
    const segments = normalized.split('/').filter(Boolean);
    return segments[segments.length - 1] || normalized;
  }

  private buildHeaders(skipLoader = false): HttpHeaders | null {
    if (this.authState.isAdmin()) {
      if (!skipLoader) return null;
      return new HttpHeaders().set('X-Skip-Loader', '1');
    }

    const companyId = this.companyState.membership()?.company?.id;
    if (!companyId) {
      if (!skipLoader) return null;
      return new HttpHeaders().set('X-Skip-Loader', '1');
    }

    let headers = new HttpHeaders().set('X-Selected-Company-ID', String(companyId));
    if (skipLoader) {
      headers = headers.set('X-Skip-Loader', '1');
    }
    return headers;
  }
}
